'use client';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';
import { Listing, School } from 'types/collections';

import { ImageUpload } from '@components/ImageUpload';
import { showToast } from '@components/ToastContainer';

import { FieldErrors, ListingData, validateListingForm } from '@utils/helpers/schoolListingHelpers';

import { SchoolHeader } from './SchoolHeader';
import { ContentWrapper } from './styles';
import { Textarea } from './Textarea';

interface EditSchoolListingProps {
  listing?: Listing | null;
  schoolProfile?: School | null;
  onListingChange?: (listing: Partial<Listing>) => void;
}

export interface EditSchoolListingRef {
  validateForm: () => boolean;
}

/**
 * Edit listing component
 */
export const EditSchoolListing = forwardRef<EditSchoolListingRef, EditSchoolListingProps>(
  ({ listing, schoolProfile, onListingChange }, ref) => {
    const { t } = useTranslation('common');
    const queryClient = useQueryClient();

    // Local state for form values
    const [formData, setFormData] = useState<ListingData>({
      intro: listing?.intro || '',
      role: listing?.role || '',
      rules: listing?.rules || '',
      supplies: listing?.supplies || '',
    });

    // Track if photo has been uploaded in this session
    const [hasUploadedPhoto, setHasUploadedPhoto] = useState(false);

    // Track if the existing photo failed to load (corrupted/deleted)
    const [photoLoadFailed, setPhotoLoadFailed] = useState(false);

    // State for field errors
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
      intro: false,
      role: false,
      rules: false,
      supplies: false,
      photo: false,
    });

    // Determine if photo exists and is valid:
    // - Must have been uploaded in this session OR
    // - Must exist in profile AND not have failed to load
    const hasValidPhoto =
      hasUploadedPhoto || (Boolean(schoolProfile?.photo_path) && !photoLoadFailed);

    // Expose validation function to parent
    useImperativeHandle(
      ref,
      () => ({
        validateForm: () => {
          //Check photo FIRST before other validation
          if (!hasValidPhoto) {
            setFieldErrors((prev) => ({ ...prev, photo: true }));
            showToast('error', t('schoolListing.photo'), t('schoolListing.required'));
            return false;
          }

          const photoPath = hasValidPhoto ? 'uploaded' : null;
          const { isValid, errors } = validateListingForm(formData, t, photoPath);
          setFieldErrors(errors);
          return isValid;
        },
      }),
      [formData, t, hasValidPhoto],
    );

    // Update form data when listing prop changes
    useEffect(() => {
      if (listing) {
        setFormData({
          intro: listing.intro || '',
          role: listing.role || '',
          rules: listing.rules || '',
          supplies: listing.supplies || '',
        });
      }
    }, [listing]);

    // Handle textarea changes
    const handleTextareaChange =
      (field: keyof ListingData) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const updatedData = { ...formData, [field]: value };
        setFormData(updatedData);
        onListingChange?.(updatedData);

        // Clear error when user types valid content
        if (fieldErrors[field] && value.trim().length > 0) {
          setFieldErrors((prev) => ({ ...prev, [field]: false }));
        }
      };

    // Handle blur events for individual field validation
    const handleBlur = (field: keyof ListingData) => (e: React.FocusEvent<HTMLTextAreaElement>) => {
      const value = e.target.value.trim();

      if (!value) {
        setFieldErrors((prev) => ({ ...prev, [field]: true }));
      }
    };

    const handleImageError = (error: string) => {
      // Mark that the photo failed to load - this will require re-upload
      // Don't show toast here - only show when user tries to publish
      setPhotoLoadFailed(true);
      setFieldErrors((prev) => ({ ...prev, photo: true }));
    };

    const handleImageUpload = () => {
      // Mark photo as uploaded, clear load failure, and clear error
      setHasUploadedPhoto(true);
      setPhotoLoadFailed(false);
      if (fieldErrors.photo) {
        setFieldErrors((prev) => ({ ...prev, photo: false }));
      }

      // Refresh school profile to get the new photo_path
      queryClient.invalidateQueries({ queryKey: ['school-profile'] });
    };

    return (
      <ContentWrapper>
        <ImageUpload
          onError={handleImageError}
          onImageUpload={handleImageUpload}
          uploadText={t('updatePhoto')}
          buttonName={t('updateFoto')}
          variant={'secondary'}>
          <SchoolHeader schoolProfile={schoolProfile || null} />
        </ImageUpload>
        <Textarea
          id="intro"
          placeholder={t('schoolListing.writeIntroduction')}
          name="intro"
          label={t('introduction')}
          hasError={fieldErrors.intro}
          required
          value={formData.intro}
          onChange={handleTextareaChange('intro')}
          onBlur={handleBlur('intro')}
        />
        <Textarea
          id="role"
          placeholder={t('schoolListing.writeFunctions')}
          name="role"
          label={t('schoolListing.function')}
          hasError={fieldErrors.role}
          required
          value={formData.role}
          onChange={handleTextareaChange('role')}
          onBlur={handleBlur('role')}
        />
        <Textarea
          id="rules"
          placeholder={t('schoolListing.writeReglament')}
          name="rules"
          label={t('schoolListing.regulation')}
          hasError={fieldErrors.rules}
          required
          value={formData.rules}
          onChange={handleTextareaChange('rules')}
          onBlur={handleBlur('rules')}
        />
        <Textarea
          id="supplies"
          placeholder={t('schoolListing.writeSupplies')}
          name="supplies"
          label={t('schoolListing.supplies')}
          hasError={fieldErrors.supplies}
          required
          value={formData.supplies}
          onChange={handleTextareaChange('supplies')}
          onBlur={handleBlur('supplies')}
        />
      </ContentWrapper>
    );
  },
);
